import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Canvas, ThreeEvent, useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useTranslation } from 'react-i18next';

/**
 * 3D camera-control widget for Variation · change-angle mode.
 *
 * Three independent tracks, each driving exactly one parameter:
 *
 *   azimuth   (horizontal_angle)   0 … 360°  — magenta HORIZONTAL ring (XZ plane)
 *   elevation (vertical_angle)   -30 … 90°  — green FIXED vertical arc (YZ plane)
 *   distance  (zoom)               0 … 10   — yellow vertical axis (Y line)
 *
 * The tracks live on independent geometric axes so dragging one handle
 * never repositions another visually — that was the bug with the old
 * "elevation arc rotates with azimuth" layout, where changing azimuth
 * looked like it was moving the elevation handle too.
 *
 * Drag model: on pointerdown, capture the starting cursor + starting
 * value; every pointermove computes delta pixels and maps to value
 * delta. Reliable and predictable — no raycasting needed.
 */

const R_RING = 1.3;            // Azimuth ring radius (world units)
// Elevation arc lives inside the azimuth ring so the two tracks never
// touch (they shared R=1.3 before, which caused a visible intersection
// point at the front of the widget at elevation=0).
const R_ARC = 1.05;
const D_AXIS_TOP = 1.55;       // World-Y height of the distance axis top
const DISTANCE_SCALE = D_AXIS_TOP / 10; // 0..10 → 0..D_AXIS_TOP
const IMAGE_SIZE = 1.1;        // Edge length of the image plane
const IMAGE_Y = 0;             // Image plane vertical offset (ring passes through it)

// Whole-scene translation applied via a group wrapper. All geometry
// inside the group inherits this offset. Anything that computes
// WORLD-space coordinates (e.g. the angular drag center) has to add
// this offset to local positions.
const SCENE_OFFSET = new THREE.Vector3(0, 0.2, 0);

const AZIMUTH_COLOR = '#EC4899';
const ELEVATION_COLOR = '#22D3AA';
const DISTANCE_COLOR = '#F59E0B';

// Pixels along the handle's ON-SCREEN track direction per unit of value.
// Because we now project the world tangent to screen space at drag-
// start, these correspond to actual visible drag distance along the
// track regardless of the camera's viewpoint — "drag along the ring"
// always moves the ring handle forward.
const AZIMUTH_PX_PER_DEG = 0.8;
const ELEVATION_PX_PER_DEG = 1.6;
const DISTANCE_PX_PER_UNIT = 24;

/**
 * Theme mode, read from the root class the app toggles. The scene's light
 * intensities are part of the three.js config and must keep following the
 * theme, so this watches the class rather than reading it once.
 */
function useIsDark(): boolean {
    const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));
    useEffect(() => {
        const root = document.documentElement;
        const mo = new MutationObserver(() => setIsDark(root.classList.contains('dark')));
        mo.observe(root, { attributes: true, attributeFilter: ['class'] });
        return () => mo.disconnect();
    }, []);
    return isDark;
}

interface CameraControl3DProps {
    sourceImageUrl: string;
    azimuth: number;
    elevation: number;
    distance: number;
    onChange: (next: { azimuth: number; elevation: number; distance: number }) => void;
    /**
     * When true, hide the portal entirely (display: none). Used by
     * the workspace to step the widget aside when modal overlays are
     * open (Browse projects sheet, Product picker panel, etc.) — the
     * portal is on document.body at zIndex 900 so it would otherwise
     * float above any in-workspace sheet regardless of that sheet's
     * own zIndex. Hiding sidesteps the stacking-context fight.
     */
    hidden?: boolean;
}

export const CameraControl3D: React.FC<CameraControl3DProps> = ({
    sourceImageUrl,
    azimuth,
    elevation,
    distance,
    onChange,
    hidden,
}) => {
    const { t } = useTranslation();
    const valuesRef = useRef({ azimuth, elevation, distance });
    valuesRef.current = { azimuth, elevation, distance };
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const hiddenRef = useRef<boolean>(!!hidden);
    hiddenRef.current = !!hidden;
    const [dragAxis, setDragAxis] = useState<'azimuth' | 'elevation' | 'distance' | null>(null);
    const isDark = useIsDark();

    // ─── Portal-based rendering — details in `return` below ─────────
    //
    // The workspace canvas wraps the recipe node in a CSS `transform:
    // scale()` for pan/zoom. Every WebGL sizing path R3F uses inside
    // a transformed subtree reads post-transform dimensions via
    // getBoundingClientRect, which makes the framebuffer / viewport
    // desync from the CSS square we paint to — that's the source of
    // the "widget is displaced / stretched when I zoom the workspace"
    // bug across every prior attempt to patch sizing.
    //
    // Fix: the Canvas is rendered through a React portal directly to
    // document.body. An in-node PLACEHOLDER div reserves the layout
    // slot (visible bg color stays there during init) and is tracked
    // each animation frame — the portal is positioned + sized to
    // exactly overlay the placeholder's visible rect via fixed
    // positioning. Because the portal is outside the transformed
    // workspace subtree, R3F sees a stable square viewport with
    // no transform skew.
    const placeholderRef = useRef<HTMLDivElement>(null);
    const portalRef = useRef<HTMLDivElement>(null);
    const [sideLen, setSideLen] = useState(0);
    /** The placeholder's position, so the portal is placed without the loop. */
    const [spot, setSpot] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        let rafId = 0;
        const tick = () => {
            const placeholder = placeholderRef.current;
            const portal = portalRef.current;
            if (placeholder && portal) {
                const r = placeholder.getBoundingClientRect();
                const left = Math.round(r.left);
                const top = Math.round(r.top);
                const w = Math.round(r.width);
                const h = Math.round(r.height);
                portal.style.left = `${left}px`;
                portal.style.top = `${top}px`;
                portal.style.width = `${w}px`;
                portal.style.height = `${h}px`;

                // Clip the portal to the workspace viewport's rect so
                // the widget behaves like every other node — panning a
                // node beyond the workspace edge clips it instead of
                // floating it over the sidebar / other UI. The nearest
                // [data-cs-canvas-viewport] ancestor marks the clip
                // region. If we can't find one (e.g. unit tests), skip
                // clipping.
                const viewport = placeholder.closest<HTMLElement>('[data-cs-canvas-viewport]');
                let onScreen = w > 0 && h > 0;
                if (viewport && onScreen) {
                    const v = viewport.getBoundingClientRect();
                    // Check that placeholder and viewport intersect at all.
                    onScreen = left + w > v.left && top + h > v.top
                        && left < v.right && top < v.bottom;
                    if (onScreen) {
                        // inset(top right bottom left) — distances from
                        // each edge of the portal to clip inward.
                        const clipTop = Math.max(0, v.top - top);
                        const clipRight = Math.max(0, (left + w) - v.right);
                        const clipBottom = Math.max(0, (top + h) - v.bottom);
                        const clipLeft = Math.max(0, v.left - left);
                        portal.style.clipPath =
                            `inset(${clipTop}px ${clipRight}px ${clipBottom}px ${clipLeft}px)`;
                    }
                } else if (!viewport) {
                    portal.style.clipPath = 'none';
                }
                // Prop-driven `hidden` takes precedence (modals/overlays).
                // Otherwise, track on-screen status so the widget hides
                // when the placeholder pans out of the workspace.
                portal.style.visibility = (hiddenRef.current || !onScreen) ? 'hidden' : 'visible';
                // NOTE: sizing is no longer done here — see the observer below.
                // It used to be, and that made the Canvas depend on the portal
                // existing, which is a chain with no reason to be one.
            }
            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    /**
     * The square's width, measured straight off the placeholder.
     *
     * This used to be read inside the positioning loop, which runs only when
     * BOTH the placeholder and the portal exist — so the Canvas, gated on
     * having a size, was chained behind the portal having mounted. Any hiccup
     * in that chain left `sideLen` at 0 for ever, and the widget rendered as an
     * empty grey square with no error and nothing to look at. The placeholder's
     * own size is the only thing this needs, and an observer reports it
     * directly.
     */
    useEffect(() => {
        const el = placeholderRef.current;
        if (!el) return;
        const measure = () => {
            const r = el.getBoundingClientRect();
            const w = Math.round(r.width);
            if (w > 0) setSideLen((prev) => (prev === w ? prev : w));
            // Where it belongs, from React rather than from the loop. The
            // frame loop still tracks panning; this is what makes the widget
            // land in the right place on the very first paint, and stay
            // somewhere sensible if that loop never runs at all.
            setSpot((prev) => {
                const next = { left: Math.round(r.left), top: Math.round(r.top) };
                return prev.left === next.left && prev.top === next.top ? prev : next;
            });
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        // Panning the workspace moves the placeholder without resizing it, so
        // the observer alone would go stale.
        window.addEventListener('scroll', measure, true);
        window.addEventListener('resize', measure);
        return () => {
            ro.disconnect();
            window.removeEventListener('scroll', measure, true);
            window.removeEventListener('resize', measure);
        };
    }, []);

    const sized = sideLen > 0;

    // Scene body — the Canvas + readout pills. Rendered inside the
    // portal (outside the workspace transform) so R3F never sees a
    // skewed viewport.
    /**
     * Can this browser give us a WebGL context at all?
     *
     * A blank square is the one outcome that says nothing. Contexts are a
     * limited resource — a canvas with several 3D widgets open can exhaust
     * them, and the failure is silent — so it is worth asking before drawing
     * and saying so plainly when the answer is no.
     */
    const webglOk = useMemo(() => {
        try {
            const probeCanvas = document.createElement('canvas');
            return !!(probeCanvas.getContext('webgl2') || probeCanvas.getContext('webgl'));
        } catch {
            return false;
        }
    }, []);

    const sceneBody = (
        <>
            {/**
             * When there is no Canvas, say so HERE.
             *
             * This readout started life in the placeholder — which the portal
             * covers as soon as it has a size and a position, so it could never
             * actually be read. A diagnostic behind an opaque layer is not a
             * diagnostic.
             */}
            {(!sized || !webglOk) && (
                <div className="flex h-full w-full items-center justify-center px-4 text-center text-[11px] leading-normal text-muted-foreground">
                    {!webglOk
                        ? t('CONTENT_STUDIO.CAMERA_NO_WEBGL',
                            '3D view unavailable — this browser gave no WebGL context. Closing other 3D panels usually frees one.')
                        : t('CONTENT_STUDIO.CAMERA_NO_SIZE', 'Measuring the 3D view…')}
                </div>
            )}
            {sized && webglOk && <Canvas
                // Set the camera at creation time so there's no flash of
                // the default R3F camera (position 0,0,5 looking at
                // origin). lookAt + aspect are re-enforced every frame
                // by <SceneCamera/>.
                camera={{ position: [2.5, 1.7, 2.5], fov: 50, near: 0.1, far: 100 }}
                // Render at the device's native pixel density (Retina /
                // high-DPI displays). Capping at 2x softens the thin
                // tube tracks on 3x screens; letting it run at the
                // display's actual DPR keeps them crisp.
                dpr={Math.min(window.devicePixelRatio || 1, 3)}
                gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
            >
                <SceneCamera />

                <ambientLight intensity={isDark ? 0.75 : 1.0} />
                <directionalLight position={[4, 6, 4]} intensity={isDark ? 0.55 : 0.8} />

                {/* Shift everything up + slightly left so the widget is
                    visually centered in the viewport. Without this the
                    lower-right quadrant clips against the canvas edge
                    (the camera's isometric-ish angle biases the scene
                    toward the bottom-right in screen space). */}
                <group position={SCENE_OFFSET}>
                    <ImagePlane url={sourceImageUrl} />

                    {/* Tracks — azimuth ring and elevation arc are FIXED
                        geometry (the rings themselves never move). The
                        distance ray rotates with az/el so it always points
                        toward the current virtual-camera location. */}
                    <AzimuthRing />
                    <ElevationArc />
                    <DistanceRay azimuth={azimuth} elevation={elevation} distance={distance} />

                    {/* Virtual-camera indicator — a tiny camera-shaped mesh
                        positioned at the combined spherical coord so the
                        user sees where the reshoot viewpoint is sitting and
                        which way it's pointing. Purely visual; not draggable. */}
                    <CameraGhost
                        azimuth={azimuth}
                        elevation={elevation}
                        distance={distance}
                    />

                <Handle
                    axis="azimuth"
                    position={posOnAzimuthRing(azimuth)}
                    // angularCenter: WORLD-space pivot the cursor's
                    // screen angle is measured around. Image plane
                    // center + scene offset so "swirl the cursor
                    // around the widget" rotates the handle directly.
                    // `.project(camera)` ignores ancestor transforms,
                    // so we add SCENE_OFFSET manually here.
                    angularCenter={new THREE.Vector3(0, IMAGE_Y, 0).add(SCENE_OFFSET)}
                    currentValue={azimuth}
                    pxPerUnit={AZIMUTH_PX_PER_DEG}
                    wrap
                    color={AZIMUTH_COLOR}
                    dragging={dragAxis === 'azimuth'}
                    onDragStart={() => setDragAxis('azimuth')}
                    onDragEnd={() => setDragAxis(null)}
                    onChange={(nextAz) =>
                        onChangeRef.current({ ...valuesRef.current, azimuth: nextAz })
                    }
                />
                <Handle
                    axis="elevation"
                    position={posOnElevationArc(elevation)}
                    worldTangent={tangentElevation(elevation)}
                    currentValue={elevation}
                    pxPerUnit={ELEVATION_PX_PER_DEG}
                    min={-30}
                    max={90}
                    color={ELEVATION_COLOR}
                    dragging={dragAxis === 'elevation'}
                    onDragStart={() => setDragAxis('elevation')}
                    onDragEnd={() => setDragAxis(null)}
                    onChange={(nextEl) =>
                        onChangeRef.current({ ...valuesRef.current, elevation: nextEl })
                    }
                />
                <Handle
                    axis="distance"
                    position={posOnDistanceRay(azimuth, elevation, distance)}
                    worldTangent={tangentDistance(azimuth, elevation)}
                    currentValue={distance}
                    pxPerUnit={DISTANCE_PX_PER_UNIT}
                    min={0}
                    max={10}
                    color={DISTANCE_COLOR}
                    dragging={dragAxis === 'distance'}
                    onDragStart={() => setDragAxis('distance')}
                    onDragEnd={() => setDragAxis(null)}
                    onChange={(nextD) =>
                        onChangeRef.current({ ...valuesRef.current, distance: nextD })
                    }
                />
                </group>
            </Canvas>}

            {/* Readouts overlay the 3D viewport — an LTR coordinate space. */}
            <div className="pointer-events-none absolute right-1.5 bottom-1.5 left-1.5 flex justify-center gap-1 text-xs text-foreground">
                <ReadoutPill color={AZIMUTH_COLOR} label={t('CONTENT_STUDIO.CAMERA_AZ_SHORT')} value={`${Math.round(azimuth)}°`} />
                <ReadoutPill color={ELEVATION_COLOR} label={t('CONTENT_STUDIO.CAMERA_EL_SHORT')} value={`${Math.round(elevation)}°`} />
                <ReadoutPill color={DISTANCE_COLOR} label="D" value={distance.toFixed(1)} />
            </div>
        </>
    );

    return (
        <>
            {/* Placeholder inside the recipe node — reserves the layout
                slot with the same square aspect as before so the rest
                of the node lays out naturally. The actual 3D surface
                is portaled (below) to escape the workspace transform. */}
            <div
                ref={placeholderRef}
                className="flex aspect-square w-full items-center justify-center overflow-hidden bg-muted px-4 text-center text-[11px] leading-normal text-muted-foreground"
            >
            </div>
            {/* Portal to document.body — the Canvas lives here, outside
                the workspace's CSS transform, and is absolutely
                positioned to overlay the placeholder each frame (via
                rAF). Pointer events route to the portal directly. */}
            {mounted && createPortal(
                <div
                    ref={portalRef}
                    onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                    onContextMenu={(e: React.MouseEvent) => e.preventDefault()}
                    className="fixed z-[900] overflow-hidden bg-muted"
                    style={{
                        /**
                         * A size that does not depend on the loop.
                         *
                         * left/top/width/height are set every frame by the
                         * positioner, which only runs once BOTH the placeholder
                         * and this portal exist. If that never happened the
                         * portal stayed 0x0 and the widget was invisible with
                         * nothing to see and nothing logged. Starting from the
                         * measured square means a positioning failure shows up
                         * as a widget in the wrong place — which is a bug you
                         * can see and report.
                         */
                        left: spot.left || undefined,
                        top: spot.top || undefined,
                        width: sideLen || undefined,
                        height: sideLen || undefined,
                        // Hidden via visibility (not display:none) while
                        // an overlay is open. display:'none' pauses
                        // WebGL rendering; when we flip back to 'block'
                        // there's a one-frame gap where the canvas
                        // shows its background color (dark in dark
                        // mode — the "black flash" on lightbox close).
                        // visibility:'hidden' keeps WebGL rendering
                        // underneath, so unhiding is instant + seamless.
                        visibility: hidden ? 'hidden' : 'visible',
                        pointerEvents: hidden ? 'none' : 'auto',
                    }}
                >
                    {sceneBody}
                </div>,
                document.body,
            )}
        </>
    );
};

CameraControl3D.displayName = 'CameraControl3D';

// ══ Camera that looks at the scene centroid ═══════════════════════════════

/**
 * Sets the camera position + lookAt once on mount so the widget is centered.
 * Positioned in the +X / +Y / +Z octant so all three tracks are visible:
 * the horizontal ring, the vertical arc (YZ plane), and the Y-axis
 * distance line. lookAt is slightly above origin so the distance axis
 * and image plane share the optical center.
 */
// Camera pulled in so the scene fills ~80% of the frame instead of
// floating ~50% surrounded by empty space. Same isometric angle,
// just closer — ratio preserved so the visible composition is
// identical, only zoomed in.
const CAMERA_POS = new THREE.Vector3(2.5, 1.7, 2.5);
const CAMERA_TARGET = new THREE.Vector3(0, 0.2, 0);

/**
 * Keeps the camera fully synced every frame: position, lookAt, and
 * a LOCKED aspect ratio.
 *
 * Why lock aspect to 1 instead of tracking size: the widget's
 * container is always a CSS 1:1 square (aspectRatio: '1 / 1'). When
 * the workspace canvas is pan/zoomed, its CSS transform can make R3F
 * measure a post-transform size for our canvas element — which in
 * turn changes `camera.aspect` and visibly shifts the framing. Since
 * the container's intrinsic aspect never changes, we hard-wire the
 * camera's projection to aspect=1 and ignore any size drift. Result:
 * the 3D scene composition is identical regardless of workspace zoom.
 */
const SceneCamera: React.FC = () => {
    const { camera } = useThree();
    useFrame(() => {
        if (!camera.position.equals(CAMERA_POS)) camera.position.copy(CAMERA_POS);
        camera.lookAt(CAMERA_TARGET);
        const persp = camera as THREE.PerspectiveCamera;
        if (persp.aspect !== 1) {
            persp.aspect = 1;
            persp.updateProjectionMatrix();
        }
    });
    return null;
};

// ══ Scene elements ═════════════════════════════════════════════════════

const ImagePlane: React.FC<{ url: string }> = ({ url }) => {
    const texture = useMemo(() => {
        if (!url) return null;
        const loader = new THREE.TextureLoader();
        loader.setCrossOrigin('anonymous');
        const tex = loader.load(url);
        tex.colorSpace = THREE.SRGBColorSpace;
        return tex;
    }, [url]);
    // raycast={null} — the plane is decorative, it must NOT intercept
    // pointer events or handles behind it become unclickable (R3F's
    // default raycaster hands the event to whichever mesh is closest,
    // and a material-bearing plane counts as a hit).
    return (
        <mesh position={[0, IMAGE_Y, 0]} raycast={() => null}>
            <planeGeometry args={[IMAGE_SIZE, IMAGE_SIZE]} />
            {texture ? (
                <meshBasicMaterial map={texture} toneMapped={false} side={THREE.DoubleSide} />
            ) : (
                <meshStandardMaterial color="#333" />
            )}
        </mesh>
    );
};

/** Horizontal ring at y=0 — the azimuth track (fixed geometry). */
const AzimuthRing: React.FC = () => {
    const points = useMemo(() => {
        const arr: THREE.Vector3[] = [];
        const seg = 96;
        for (let i = 0; i <= seg; i++) {
            const t = (i / seg) * Math.PI * 2;
            // Use the same (sin, cos) convention as posOnAzimuthRing so
            // the handle slides along the ring it's drawn on.
            arr.push(new THREE.Vector3(Math.sin(t) * R_RING, 0, Math.cos(t) * R_RING));
        }
        return arr;
    }, []);
    return <TubeTrack points={points} closed color={AZIMUTH_COLOR} />;
};

/**
 * Fixed vertical arc in the YZ plane (independent of azimuth) — the
 * elevation track. Spans -30° to 90° at the front of the scene so the
 * handle always sits in front of the image plane.
 */
const ElevationArc: React.FC = () => {
    const points = useMemo(() => {
        const arr: THREE.Vector3[] = [];
        const seg = 64;
        for (let i = 0; i <= seg; i++) {
            const t = i / seg;
            const elDeg = -30 + t * 120;
            const el = THREE.MathUtils.degToRad(elDeg);
            arr.push(new THREE.Vector3(0, Math.sin(el) * R_ARC, Math.cos(el) * R_ARC));
        }
        return arr;
    }, []);
    return <TubeTrack points={points} color={ELEVATION_COLOR} />;
};

/**
 * Radial line from the image plane center to the virtual camera's
 * location — the distance track. Rotates with azimuth + elevation so
 * it always points toward the current reshoot viewpoint. The distance
 * handle sits at the far end; the camera ghost sits just past it.
 */
const DistanceRay: React.FC<{
    azimuth: number;
    elevation: number;
    distance: number;
}> = ({ azimuth, elevation, distance }) => {
    const points = useMemo(() => {
        // Interpolate a few segments so TubeGeometry renders smoothly
        // (it interpolates via CatmullRomCurve3 which needs ≥3 points).
        const start = new THREE.Vector3(0, IMAGE_Y, 0);
        const end = posOnDistanceRay(azimuth, elevation, distance);
        const mid = start.clone().lerp(end, 0.5);
        const q1 = start.clone().lerp(end, 0.25);
        const q3 = start.clone().lerp(end, 0.75);
        return [start, q1, mid, q3, end];
    }, [azimuth, elevation, distance]);
    return <TubeTrack points={points} color={DISTANCE_COLOR} radius={0.013} />;
};

/**
 * Tiny camera-shaped mesh that indicates where the virtual camera is
 * pointing. Position is the combined spherical coord (az, el, distance);
 * orientation is set so the camera's lens faces the image plane.
 *
 *   [body box]──▶[lens cylinder]   ... pointed toward origin
 */
const CameraGhost: React.FC<{
    azimuth: number;
    elevation: number;
    distance: number;
}> = ({ azimuth, elevation, distance }) => {
    const groupRef = useRef<THREE.Group>(null);
    // Visual radius: the camera sits on a sphere whose radius scales with
    // the distance value, offset from the image plane a bit so distance=0
    // doesn't overlap the plane. Center of the sphere is IMAGE_Y so the
    // camera orbits the image plane. Same (sin, cos) convention as
    // posOnAzimuthRing — az=0 sits in front of the image (+Z).
    const radius = 0.55 + distance * 0.12; // ~0.55 at d=0, ~1.75 at d=10
    const pos = useMemo(() => {
        const az = THREE.MathUtils.degToRad(azimuth);
        const el = THREE.MathUtils.degToRad(elevation);
        return new THREE.Vector3(
            Math.cos(el) * radius * Math.sin(az),
            IMAGE_Y + Math.sin(el) * radius,
            Math.cos(el) * radius * Math.cos(az),
        );
    }, [azimuth, elevation, radius]);

    // Orient so the lens (local +Z of the group) points at the image
    // plane center. The ghost lives INSIDE the SCENE_OFFSET group,
    // but `lookAt` expects a WORLD-space target — image plane world
    // position is SCENE_OFFSET + (0, IMAGE_Y, 0). Using useFrame so
    // the rotation updates every frame alongside the scene graph,
    // avoiding useEffect's race with matrixWorld propagation.
    useFrame(() => {
        const g = groupRef.current;
        if (!g) return;
        g.position.copy(pos);
        g.lookAt(SCENE_OFFSET.x, SCENE_OFFSET.y + IMAGE_Y, SCENE_OFFSET.z);
    });

    return (
        <group ref={groupRef}>
            {/* Body — small rounded box */}
            <mesh>
                <boxGeometry args={[0.18, 0.14, 0.22]} />
                <meshStandardMaterial
                    color="#F8FAFC"
                    metalness={0.5}
                    roughness={0.35}
                    emissive="#FFFFFF"
                    emissiveIntensity={0.08}
                />
            </mesh>
            {/* Lens — cylinder sticking out along +Z (toward origin after lookAt) */}
            <mesh position={[0, 0, 0.14]} rotation={[Math.PI / 2, 0, 0]}>
                <cylinderGeometry args={[0.055, 0.07, 0.1, 20]} />
                <meshStandardMaterial
                    color="#1F2937"
                    metalness={0.6}
                    roughness={0.25}
                />
            </mesh>
            {/* Lens inner — a tiny bright disk so the "eye" shows up */}
            <mesh position={[0, 0, 0.195]} rotation={[Math.PI / 2, 0, 0]}>
                <cylinderGeometry args={[0.04, 0.04, 0.01, 20]} />
                <meshBasicMaterial color="#67E8F9" toneMapped={false} />
            </mesh>
            {/* Viewfinder hump on top */}
            <mesh position={[0, 0.085, -0.03]}>
                <boxGeometry args={[0.08, 0.05, 0.06]} />
                <meshStandardMaterial color="#E2E8F0" roughness={0.4} />
            </mesh>
        </group>
    );
};

// ══ Handle — screen-delta drag (no raycasting) ═════════════════════════

type DragAxis = 'azimuth' | 'elevation' | 'distance';

interface HandleProps {
    axis: DragAxis;
    position: THREE.Vector3;
    /** World-space unit direction along the track at the current value
     *  (the derivative of position w.r.t. value). At drag-start we
     *  project this vector onto the screen to get the 2D direction the
     *  cursor should move in to INCREASE the value. Unused for rotational
     *  handles that use angular drag around a center point. */
    worldTangent?: THREE.Vector3;
    /** Current value — captured at pointerdown as the drag anchor. */
    currentValue: number;
    /** How many pixels of on-screen drag correspond to +1 unit of value.
     *  For angular-drag handles (azimuth) this is replaced by a rad→deg
     *  mapping (1 rad of cursor rotation = 1 rad / 57.3°). */
    pxPerUnit: number;
    /** Minimum value (ignored for wrap-around axes like azimuth). */
    min?: number;
    max?: number;
    /** Wrap into [0, 360) — used for azimuth. */
    wrap?: boolean;
    /** If set, drag is ANGULAR around this world-space center instead of
     *  linear along a projected tangent. The user's cursor rotation
     *  around the center (in screen pixels) maps directly to value
     *  rotation — best for circular tracks. */
    angularCenter?: THREE.Vector3;
    color: string;
    dragging: boolean;
    onDragStart: () => void;
    onDragEnd: () => void;
    onChange: (next: number) => void;
}

const Handle: React.FC<HandleProps> = ({
    position,
    worldTangent,
    angularCenter,
    currentValue,
    pxPerUnit,
    min,
    max,
    wrap,
    color,
    dragging,
    onDragStart,
    onDragEnd,
    onChange,
}) => {
    const { gl, camera } = useThree();
    const [hovered, setHovered] = useState(false);
    // Refs so global pointermove always sees the latest props without
    // re-binding listeners mid-drag.
    const currentValueRef = useRef(currentValue);
    currentValueRef.current = currentValue;
    const worldTangentRef = useRef(worldTangent);
    worldTangentRef.current = worldTangent;
    const positionRef = useRef(position);
    positionRef.current = position;
    const angularCenterRef = useRef(angularCenter);
    angularCenterRef.current = angularCenter;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const handlePointerDown = (e: ThreeEvent<PointerEvent>) => {
        e.stopPropagation();
        onDragStart();
        const startX = (e.nativeEvent as PointerEvent).clientX;
        const startY = (e.nativeEvent as PointerEvent).clientY;
        const startValue = currentValueRef.current;
        const rect = gl.domElement.getBoundingClientRect();
        const toScreen = (p: THREE.Vector3) => {
            const ndc = p.clone().project(camera);
            return {
                x: rect.left + ((ndc.x + 1) / 2) * rect.width,
                y: rect.top + ((1 - ndc.y) / 2) * rect.height,
            };
        };

        let moveHandler: (ev: PointerEvent) => void;

        if (angularCenterRef.current) {
            // ─── Angular drag — for rotational handles (azimuth) ───
            // Track the angle of (cursor − screenCenter) and accumulate
            // frame-to-frame deltas. Allows unlimited rotation (multiple
            // full turns) without snapping, so vendors can swirl their
            // cursor freely in any direction to change the value.
            //
            // Sign: atan2 in screen space (Y grows downward) increases
            // COUNTER-clockwise visually, but the handle's visible motion
            // around the ring is CLOCKWISE for increasing azimuth under
            // our isometric camera. So we NEGATE the accumulated angle
            // — cursor sweeping the same direction the handle moves
            // feels natural and increases the value.
            const center = toScreen(angularCenterRef.current);
            let lastAngle = Math.atan2(startY - center.y, startX - center.x);
            let accumulated = 0;
            moveHandler = (ev) => {
                const angle = Math.atan2(ev.clientY - center.y, ev.clientX - center.x);
                let frameDelta = angle - lastAngle;
                if (frameDelta > Math.PI) frameDelta -= 2 * Math.PI;
                if (frameDelta < -Math.PI) frameDelta += 2 * Math.PI;
                accumulated += frameDelta;
                lastAngle = angle;
                let next = startValue - THREE.MathUtils.radToDeg(accumulated);
                if (wrap) next = ((next % 360) + 360) % 360;
                else if (min != null && max != null) next = Math.max(min, Math.min(max, next));
                onChangeRef.current(next);
            };
        } else {
            // ─── Tangent-projection drag — for linear handles ───────
            // Cumulative model (fast + smooth) with re-anchoring on
            // clamp: when the value hits min/max, we move the drag
            // origin to the current cursor/value pair so "overshoot"
            // cursor motion doesn't accumulate past the clamp. Reverse
            // drag un-clamps immediately from the rebased anchor
            // instead of snapping back to the original start value.
            const here = toScreen(positionRef.current);
            const tangent = worldTangentRef.current || new THREE.Vector3(1, 0, 0);
            const nearby = toScreen(positionRef.current.clone().add(tangent));
            let dirX = nearby.x - here.x;
            let dirY = nearby.y - here.y;
            const len = Math.hypot(dirX, dirY) || 1;
            dirX /= len;
            dirY /= len;
            let anchorValue = startValue;
            let anchorAlongPx = 0;
            moveHandler = (ev) => {
                const dx = ev.clientX - startX;
                const dy = ev.clientY - startY;
                const alongPx = dx * dirX + dy * dirY;
                const desired = anchorValue + (alongPx - anchorAlongPx) / pxPerUnit;
                let next = desired;
                if (wrap) next = ((next % 360) + 360) % 360;
                else if (min != null && max != null) next = Math.max(min, Math.min(max, next));
                // Rebase the anchor whenever we're AT a clamp boundary
                // (not only when `desired` strictly overshot). Without
                // this, arriving exactly at min/max leaves the anchor
                // sitting at the original drag-start value — dragging
                // the cursor back toward the start then "teleports"
                // the value back (the distance→0 snap-to-5 bug).
                const atMin = min != null && next <= min + 1e-9;
                const atMax = max != null && next >= max - 1e-9;
                if (atMin || atMax) {
                    anchorValue = next;
                    anchorAlongPx = alongPx;
                }
                onChangeRef.current(next);
            };
        }

        const upHandler = () => {
            onDragEnd();
            window.removeEventListener('pointermove', moveHandler);
            window.removeEventListener('pointerup', upHandler);
            gl.domElement.style.cursor = 'default';
        };
        window.addEventListener('pointermove', moveHandler);
        window.addEventListener('pointerup', upHandler);
        gl.domElement.style.cursor = 'grabbing';
    };

    // Smooth hover / drag scale animation. Lerps toward the target
    // scale every frame — gives a gentle pop on grab and fade-back on
    // release. Same scale drives the glow shell so both inflate in sync.
    const coreRef = useRef<THREE.Mesh | null>(null);
    const glowRef = useRef<THREE.Mesh | null>(null);
    const scaleRef = useRef(1);
    const glowOpacityRef = useRef(0.14);
    const targetScale = dragging ? 1.25 : hovered ? 1.12 : 1;
    const targetGlow = dragging ? 0.35 : hovered ? 0.28 : 0.14;
    useFrame(() => {
        const ease = 0.2;
        scaleRef.current += (targetScale - scaleRef.current) * ease;
        glowOpacityRef.current += (targetGlow - glowOpacityRef.current) * ease;
        if (coreRef.current) coreRef.current.scale.setScalar(scaleRef.current);
        if (glowRef.current) {
            glowRef.current.scale.setScalar(scaleRef.current * 1.45);
            const mat = glowRef.current.material as THREE.MeshBasicMaterial;
            if (mat) mat.opacity = glowOpacityRef.current;
        }
    });

    return (
        <group position={position}>
            {/* Subtle additive glow halo — just enough to read as a
                neon dot on either background, not a bloom. */}
            <mesh ref={glowRef}>
                <sphereGeometry args={[0.09, 16, 16]} />
                <meshBasicMaterial
                    color={color}
                    transparent
                    opacity={0.14}
                    blending={THREE.AdditiveBlending}
                    depthWrite={false}
                    toneMapped={false}
                />
            </mesh>
            {/* Core visible sphere. */}
            <mesh ref={coreRef}>
                <sphereGeometry args={[0.09, 20, 20]} />
                <meshStandardMaterial
                    color={color}
                    emissive={color}
                    emissiveIntensity={dragging ? 0.85 : 0.45}
                    roughness={0.3}
                />
            </mesh>
            {/* Invisible larger hit sphere — forgiving click target. */}
            <mesh
                onPointerDown={handlePointerDown}
                onPointerOver={(e) => {
                    e.stopPropagation();
                    setHovered(true);
                    gl.domElement.style.cursor = 'grab';
                }}
                onPointerOut={() => {
                    setHovered(false);
                    if (!dragging) gl.domElement.style.cursor = 'default';
                }}
            >
                <sphereGeometry args={[0.28, 16, 16]} />
                <meshBasicMaterial transparent opacity={0} depthWrite={false} />
            </mesh>
        </group>
    );
};

// ══ Track position helpers ═════════════════════════════════════════════

/**
 * Position on the horizontal azimuth ring (y=0). Convention: az=0 is
 * DIRECTLY IN FRONT of the image plane (+Z axis), matching fal's qwen
 * multi-angle schema where horizontal_angle=0 is the original view.
 * Positive azimuth sweeps right-then-back-then-left (right-handed orbit
 * around the Y axis when viewed from above).
 */
function posOnAzimuthRing(azimuthDeg: number): THREE.Vector3 {
    const a = THREE.MathUtils.degToRad(azimuthDeg);
    return new THREE.Vector3(Math.sin(a) * R_RING, 0, Math.cos(a) * R_RING);
}

/** Position on the fixed vertical arc in the YZ plane. */
function posOnElevationArc(elevationDeg: number): THREE.Vector3 {
    const e = THREE.MathUtils.degToRad(elevationDeg);
    return new THREE.Vector3(0, Math.sin(e) * R_ARC, Math.cos(e) * R_ARC);
}

/**
 * Position on the radial distance ray — at the end of a line from the
 * image plane toward the current (azimuth, elevation) direction, with
 * length proportional to distance. Handle sits here.
 */
function posOnDistanceRay(
    azimuthDeg: number,
    elevationDeg: number,
    distance: number,
): THREE.Vector3 {
    const az = THREE.MathUtils.degToRad(azimuthDeg);
    const el = THREE.MathUtils.degToRad(elevationDeg);
    // Minimum radius keeps the handle off the image plane at distance=0.
    const r = 0.35 + distance * DISTANCE_SCALE;
    // az=0 → +Z (front of image plane). See posOnAzimuthRing comment.
    return new THREE.Vector3(
        Math.cos(el) * r * Math.sin(az),
        IMAGE_Y + Math.sin(el) * r,
        Math.cos(el) * r * Math.cos(az),
    );
}

// ── World-space tangents: the direction the handle moves for +Δvalue ──
// These are the derivatives of the position functions w.r.t. their driving
// value, normalized so they're unit vectors in world space. At drag-start
// they're projected to screen space so the cursor delta along the
// projected direction maps naturally to value changes (drag the way the
// handle wants to go → value increases).

function tangentAzimuth(azimuthDeg: number): THREE.Vector3 {
    // d/dθ of (sin(θ)R, 0, cos(θ)R) = (cos(θ)R, 0, -sin(θ)R) → unit: (cos, 0, -sin)
    const a = THREE.MathUtils.degToRad(azimuthDeg);
    return new THREE.Vector3(Math.cos(a), 0, -Math.sin(a));
}

function tangentElevation(elevationDeg: number): THREE.Vector3 {
    // d/dφ of (0, sin(φ)R, cos(φ)R) = (0, cos(φ)R, -sin(φ)R) → unit: (0, cos, -sin)
    const e = THREE.MathUtils.degToRad(elevationDeg);
    return new THREE.Vector3(0, Math.cos(e), -Math.sin(e));
}

function tangentDistance(azimuthDeg: number, elevationDeg: number): THREE.Vector3 {
    // Radial outward direction from origin in (az, el) direction — the
    // distance handle slides along this line.
    const az = THREE.MathUtils.degToRad(azimuthDeg);
    const el = THREE.MathUtils.degToRad(elevationDeg);
    return new THREE.Vector3(
        Math.cos(el) * Math.sin(az),
        Math.sin(el),
        Math.cos(el) * Math.cos(az),
    );
}

function clamp(n: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(hi, n));
}

/**
 * Track renderer — builds a smooth 3D TUBE around the input points so
 * the ring / arc / ray read as real geometry instead of 1-pixel WebGL
 * lines (which can't be thickened reliably across platforms).
 *
 * Uses meshBasicMaterial with toneMapped: false for a flat vector-like
 * color rendering — meshStandardMaterial with low-intensity emissive
 * was smudging the silhouette under the scene's soft lighting, making
 * the tubes look fuzzy instead of crisp.
 */
const TubeTrack: React.FC<{
    points: THREE.Vector3[];
    color: string;
    radius?: number;
    closed?: boolean;
    opacity?: number;
}> = ({ points, color, radius = 0.016, closed = false, opacity = 1 }) => {
    const geom = useMemo(() => {
        const curve = new THREE.CatmullRomCurve3(points, closed, 'catmullrom', 0.5);
        // Dense tubular + radial segments keep the silhouette smooth
        // even when the camera zooms in. Ring + arc get the full
        // resolution; on smaller straight-ish tracks (distance ray)
        // this is overkill but cheap at this scale.
        return new THREE.TubeGeometry(curve, closed ? 192 : 128, radius, 18, closed);
    }, [points, radius, closed]);
    return (
        <mesh geometry={geom}>
            <meshBasicMaterial
                color={color}
                toneMapped={false}
                transparent={opacity < 1}
                opacity={opacity}
            />
        </mesh>
    );
};

/** Track-coloured dots are data — they match the 3D track colours. */
const ReadoutPill: React.FC<{ color: string; label: string; value: string }> = ({
    color,
    label,
    value,
}) => (
    <div className="flex items-center gap-1 bg-background/85 px-1.5 py-0.5 backdrop-blur-sm">
        <div
            className="size-2 shrink-0 rounded-full"
            style={{ backgroundColor: color }}
        />
        <span className="opacity-70">{label}</span>
        <span className="font-medium">{value}</span>
    </div>
);
